import moment from 'moment'

import { getEndPlace, getPorts, getStartPlace } from './bookmarks/cruise'

import { getExperienceById } from 'api/experiences'
import { getAirportsSearch } from 'api/flights'
import * as OfferService from 'api/offer'
import { getOrderItemFlightDetails } from 'api/order'
import { getSnapshotDetailsForTourV2Offer } from 'api/tourV2'
import config from 'constants/config'
import {
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
} from 'constants/dateFormats'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import offerPageURL, { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { isTourV2Offer, isVillaOffer } from 'lib/offer/offerTypes'
import { CreateTripItemRequest } from 'tripPlanner/api/tripItem/types'
import { Place } from 'tripPlanner/api/types'
import {
  concatStringsWithComma,
  convertBedbankPropertyAddressToRegion,
  convertOfferPropertyGeoDataToRegion,
  formatDmytoIsoDate,
} from 'tripPlanner/utils'
import {
  CreateAccommodationRequestBody,
  CreateCruiseRequestBody,
  CreateExperienceRequestBody,
  CreateFlightRequestBody,
  CreateTourRequestBody,
  createTourV2Payload,
  formatBedbankCheckInOutTime,
  formatDateForPayload,
  formatTimeForPayload,
  getLeHotelCheckInAndOutTimes,
} from 'tripPlanner/utils/itemConversions/common'

const convertStandardOrderItemToAccommodationTripItem = (
  order: App.Order,
  orderItem: App.OrderItem,
  offer: App.Offer | undefined,
  currentRegionCode: string,
  orderItemIds: Array<string>,
): CreateAccommodationRequestBody => {
  const { reservation, offer: orderOffer } = orderItem
  const orderId = order.id

  const startDate = reservation?.startDate
  const endDate = reservation?.endDate

  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${currentRegionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(orderOffer)

  const imageId = offer ? offer.image.id : orderItem.offer.image.id
  let place: CreateAccommodationRequestBody['place']

  const startDateMoment = startDate && moment(startDate)
  const endDateMoment = endDate && moment(endDate)
  const [startTimeMoment, endTimeMoment] = getLeHotelCheckInAndOutTimes(offer)

  let region: string | undefined
  if (offer?.property?.geoData) {
    region = convertOfferPropertyGeoDataToRegion(offer.property.geoData)
  }

  if (offer?.property?.latitude && offer.property.longitude) {
    place = {
      type: 'GEO',
      name: offer.property.name,
      address: offer?.property?.address ?? offer.location,
      point: {
        lat: offer.property.latitude,
        lng: offer.property.longitude,
      },
      photoUrl: `${config.IMAGE_HOST}/${imageId}.jpeg`,
      region,
    }
  } else {
    place = {
      type: 'CUSTOM',
      name: orderOffer.name,
      address: offer?.property?.address ?? orderOffer.location,
      region,
    }
  }

  return {
    type: 'ACCOMMODATION',
    sourceType: isVillaOffer(offer) ? 'LE_Accommodation_Rental' : 'LE_Hotel',
    isBooked: true,
    startDate: formatDateForPayload(startDateMoment),
    startTime: formatTimeForPayload(startTimeMoment),
    endDate: formatDateForPayload(endDateMoment),
    endTime: formatTimeForPayload(endTimeMoment),
    place,
    phone: undefined,
    confirmationCode: orderId,
    confirmationItemCodes: orderItemIds,
    confirmationUrl,
    price: Number(orderItem.total),
    url: offerUrl,
    code: orderOffer.id,
    subCode:
      'roomTypeId' in orderItem.package ?
        orderItem.package.roomTypeId :
        undefined,
  }
}

interface ConvertOrderExperienceItemToExperienceTripItemParams {
  orderItem: App.OrderExperienceItem
  orderId: string
  currentCurrency: string
  currentRegionCode: string
  orderItemIds: Array<string>
}

const convertOrderExperienceItemToExperienceTripItem = async({
  orderItem,
  orderId,
  currentCurrency,
  currentRegionCode,
  orderItemIds,
}: ConvertOrderExperienceItemToExperienceTripItemParams): Promise<CreateExperienceRequestBody> => {
  const { experienceId } = orderItem

  const experience = await getExperienceById(experienceId, {
    currentCurrency,
    currentRegionCode,
  })

  // Will a user always be accessing their order from their original region when travelling?
  // The confirmationUrl will always contain a region code from where they purchased
  const regionCode = window.location.pathname.split('/')[1]
  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${regionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`

  // ToDo: Check if there is a better way of doing this.
  const offerUrl = baseUrl + getExperienceOfferPageURL(experience)

  // handle multi location experiences
  const redemptionLocation =
    experience.redemptionLocations?.find(
      (location) => location.id === orderItem.redemptionLocation?.id,
    ) ?? experience.location

  const payload: CreateExperienceRequestBody = {
    type: 'EXPERIENCE',
    name: experience.name,
    sourceType: 'LE_Experience',
    isBooked: true,
    startDate: orderItem.date,
    startTime: orderItem.time,
    endDate: orderItem.date,
    place: {
      type: 'GEO',
      name: redemptionLocation.name,
      region: redemptionLocation.name,
      point: {
        lat: redemptionLocation.latitude,
        lng: redemptionLocation.longitude,
      },
      photoUrl: experience.images[0]?.url,
    },
    confirmationCode: orderId,
    confirmationItemCodes: orderItemIds,
    confirmationUrl,
    price: experience.price,
    url: offerUrl,
    code: experience.id,
  }

  return payload
}

const convertOrderItemToTourOrCruiseTripItem = (
  order: App.Order,
  orderItem: App.OrderItem,
  offer: App.Offer,
  currentRegionCode: string,
  orderItemIds: Array<string>,
): CreateTourRequestBody | CreateCruiseRequestBody => {
  const { reservation, offer: orderOffer } = orderItem
  const orderId = order.id

  const startDate = reservation?.startDate ?
    moment(reservation.startDate).format(ISO_DATE_FORMAT) :
    undefined
  const endDate = reservation?.endDate ?
    moment(reservation.endDate).format(ISO_DATE_FORMAT) :
    undefined

  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${currentRegionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(orderOffer)

  const offerPackage = ('packages' in offer && offer?.packages[0]) ?? null
  const imageId = offer ? offer.image.id : orderItem.offer.image.id

  let startPlace: Place = {
    type: 'CUSTOM',
    name: offer.location,
    address: offer.property?.address ?? offer.locationHeading,
    photoUrl: imageId ?
      `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${imageId}.jpeg` :
      undefined,
  }

  let endPlace: Place = {
    type: 'CUSTOM',
    name: offer.location,
    address: offer.property?.address ?? offer.locationHeading,
    photoUrl: imageId ?
      `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${imageId}.jpeg` :
      undefined,
  }

  let timezone: string | undefined = undefined

  if (offerPackage && offerPackage?.tour) {
    let startPlaceRegion = ''
    if (offerPackage.tour.geoData) {
      startPlaceRegion = convertOfferPropertyGeoDataToRegion(
        offerPackage.tour.geoData,
      )
    }
    startPlace = {
      ...startPlace,
      type: 'GEO',
      point: {
        lat: offerPackage.tour.latitude,
        lng: offerPackage.tour.longitude,
      },
      region: startPlaceRegion,
    }

    let endPlaceRegion = ''
    if (offerPackage.tour.geoData) {
      endPlaceRegion = convertOfferPropertyGeoDataToRegion(
        offerPackage.tour.geoData,
      )
    }
    // ToDo: Confirm with Tour's team to make sure we have the correct start and end place.
    endPlace = {
      ...endPlace,
      type: 'GEO',
      point: {
        lat: offerPackage.tour.latitude,
        lng: offerPackage.tour.longitude,
      },
      region: endPlaceRegion,
    }

    timezone = offerPackage.tour.timezone
  }

  if (offer.holidayTypes?.includes('Cruises')) {
    const payload: CreateCruiseRequestBody = {
      type: 'CRUISE',
      name: offer.name,
      isBooked: true,
      sourceType: 'LE_Cruise',
      startDate,
      startTimezone: timezone,
      endDate,
      endTimezone: timezone,
      startPlace,
      endPlace,
      phone: undefined,
      confirmationCode: order.id,
      confirmationItemCodes: orderItemIds,
      confirmationUrl,
      price: Number(orderItem.total),
      url: offerUrl,
      code: orderOffer.id,
    }

    return payload
  }

  const payload: CreateTourRequestBody = {
    type: 'TOUR',
    name: offer.name,
    isBooked: true,
    sourceType: 'LE_Tour',
    startDate,
    endDate,
    startPlace,
    endPlace,
    phone: undefined,
    confirmationCode: order.id,
    confirmationItemCodes: orderItemIds,
    confirmationUrl,
    price: Number(orderItem.total),
    url: offerUrl,
    code: orderOffer.id,
  }

  return payload
}

const convertOrderItemToCruiseV2TripItem = (
  order: App.Order,
  orderItem: App.CruiseOrderItem,
  offer: App.CruiseOffer,
  currentRegionCode: string,
  orderItemIds: Array<string>,
): CreateCruiseRequestBody => {
  const { departureId, departureDate, arrivalDate } = orderItem
  const orderId = order.id

  const startDate = departureDate ?
    moment(departureDate).format(ISO_DATE_FORMAT) :
    undefined
  const endDate = arrivalDate ?
    moment(arrivalDate).format(ISO_DATE_FORMAT) :
    undefined

  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${currentRegionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(offer)

  const image = offer.images[0]
  const imageUrl = image ?
    `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${image.id}.jpeg` :
    undefined

  const ports = getPorts(offer)

  const startPlace = {
    ...getStartPlace(offer.departurePort, ports),
    region: offer.departurePort,
    photoUrl: imageUrl,
  }
  const endPlace = {
    ...getEndPlace(offer.returnPort, ports),
    region: offer.returnPort,
    photoUrl: imageUrl,
  }

  const payload: CreateCruiseRequestBody = {
    type: 'CRUISE',
    name: offer.name,
    isBooked: true,
    sourceType: 'LE_Cruise',
    startDate,
    endDate,
    startPlace,
    endPlace,
    confirmationCode: order.id,
    confirmationItemCodes: orderItemIds,
    confirmationUrl,
    price: Number(orderItem.total),
    url: offerUrl,
    code: offer.id,
    savedItemData: {
      regionCode: order.regionCode,
      currencyCode: order.currencyCode,
      offerType: 'cruise' as const,
      offerId: offer.id,
      departure: {
        id: departureId,
        departureDate,
      },
    },
  }

  return payload
}

const convertOrderBedbankItemToHotelTripItem = (
  bedbankItem: App.OrderBedbankItem,
): CreateAccommodationRequestBody => {
  const {
    orderId,
    checkIn: startDate,
    checkOut: endDate,
    offer,
    total: price,
    roomTypeId,
  } = bedbankItem

  const { property, name } = offer

  // Will a user always be accessing their order from their original region when travelling?
  // The confirmationUrl will always contain a the region code from where they purchased
  const regionCode = window.location.pathname.split('/')[1]
  const baseUrl = `${window.location.protocol}//${window.location.hostname}/${regionCode}`
  const confirmationUrl = baseUrl + `/booking/confirmation?orderId=${orderId}`
  const offerUrl = baseUrl + offerPageURL(offer)

  const address = `${property.address.line1}, ${property.address.city}, ${property.address.postalCode}, ${property.address.countryName}`
  const region = convertBedbankPropertyAddressToRegion(property.address)

  let checkInTime: string | undefined = startDate.format(SHORT_TIME_FORMAT_24_HOUR)
  let checkOutTime: string | undefined = endDate.format(SHORT_TIME_FORMAT_24_HOUR)

  if (checkInTime === '00:00' && offer.property.finePrint?.checkIn?.beginTime) {
    checkInTime = formatBedbankCheckInOutTime(
      offer.property.finePrint?.checkIn?.beginTime,
    )
  }

  if (checkOutTime === '00:00' && offer.property.finePrint?.checkOut?.time) {
    checkOutTime = formatBedbankCheckInOutTime(
      offer.property.finePrint?.checkOut?.time,
    )
  }

  return {
    type: 'ACCOMMODATION',
    sourceType: 'LE_Hotel_Bedbank',
    isBooked: true,
    startTime: checkInTime === '00:00' ? undefined : checkInTime,
    startDate: startDate.format(ISO_DATE_FORMAT),
    endTime: checkOutTime === '00:00' ? undefined : checkOutTime,
    endDate: endDate.format(ISO_DATE_FORMAT),
    place: {
      type: 'GEO',
      name,
      address,
      point: {
        lat: offer.property.latitude,
        lng: offer.property.longitude,
      },
      photoUrl: `${config.IMAGE_HOST}/q_auto:eco,c_fill,g_auto,w_520,ar_16:9/${offer.image.id}.jpeg`,
      region,
    },
    confirmationCode: orderId,
    confirmationItemCodes: [bedbankItem.id],
    confirmationUrl,
    price,
    url: offerUrl,
    code: offer.id,
    subCode: roomTypeId,
  }
}

export type FlightWithAirports = {
  flight: App.FlightV2
  departureAirport: App.AirportLocation | undefined
  arrivalAirport: App.AirportLocation | undefined
}

export function formatAirportLocationToRegionString(
  airportLocation: App.AirportLocation | undefined,
) {
  return concatStringsWithComma(
    airportLocation?.cityDesc,
    airportLocation?.country,
  )
}

const convertFlightItemToFlightTripItem = (
  flight: FlightWithAirports,
  orderItem: App.OrderFlightItem,
): CreateFlightRequestBody => {
  const {
    flight: {
      departureAirport,
      departureAirportName,
      arrivalAirport,
      arrivalAirportName,
      flightNumber,
      departingDate,
      departingTime,
      arrivalDate,
      arrivalTime,
      marketingAirline,
    },
    departureAirport: departureAirportObject,
    arrivalAirport: arrivalAirportObject,
  } = flight

  // Will a user always be accessing their order from their original region when travelling?
  // The confirmationUrl will always contain a the region code from where they purchased
  const regionCode = window.location.pathname.split('/')[1]
  const url = `${window.location.protocol}//${window.location.hostname}/${regionCode}`
  const orderUrl = `${url}/booking/confirmation?orderId=${orderItem.orderId}`

  const payload: CreateFlightRequestBody = {
    type: 'FLIGHT',
    sourceType: 'LE_Flight',
    originAirportCode: departureAirport,
    destinationAirportCode: arrivalAirport,
    flightNumber,
    startTime: departingTime,
    startDate: formatDmytoIsoDate(departingDate),
    endTime: arrivalTime,
    endDate: formatDmytoIsoDate(arrivalDate),
    startPlace: {
      type: 'GEO',
      name:
        departureAirportObject?.airportName ??
        departureAirportObject?.cityDesc ??
        departureAirport,
      address: departureAirportObject ?
        `${departureAirportObject.cityDesc}, ${departureAirportObject.country}` :
        departureAirportName,
      point: {
        lat: departureAirportObject?.lt ?? 0,
        lng: departureAirportObject?.lg ?? 0,
      },
      region: formatAirportLocationToRegionString(departureAirportObject),
    },
    endPlace: {
      type: 'GEO',
      name:
        arrivalAirportObject?.airportName ??
        arrivalAirportObject?.cityDesc ??
        arrivalAirport,
      address: arrivalAirportObject ?
        `${arrivalAirportObject.cityDesc}, ${arrivalAirportObject.country}` :
        arrivalAirportName,
      point: {
        lat: arrivalAirportObject?.lt ?? 0,
        lng: arrivalAirportObject?.lg ?? 0,
      },
      region: formatAirportLocationToRegionString(arrivalAirportObject),
    },
    isBooked: true,
    confirmationUrl: orderUrl,
    airlineName: marketingAirline.name,
    price: Number(orderItem.total),
    confirmationCode: orderItem.orderId,
    confirmationItemCodes: [orderItem.id],
  }

  return payload
}

const getFlightsFromFlightOrder = async(
  orderItem: App.OrderFlightItem,
): Promise<Array<FlightWithAirports>> => {
  const { id, orderId } = orderItem

  const flightDetail = await getOrderItemFlightDetails({ itemId: id, orderId })

  const flights = flightDetail.journeyFares.fares.flatMap(
    (fare) => fare.flightGroup.flights,
  )

  const flightsWithAirports: Array<FlightWithAirports> = []

  for (let index = 0; index < flights.length; index++) {
    const flight = flights[index]
    const arrivalAirports = (
      await getAirportsSearch(flight.arrivalAirport)
    ).filter((airport) => airport.airportCode === flight.arrivalAirport)
    const departureAirports = (
      await getAirportsSearch(flight.departureAirport)
    ).filter((airport) => airport.airportCode === flight.departureAirport)

    flightsWithAirports.push({
      flight,
      departureAirport:
        departureAirports.length == 1 ? departureAirports[0] : undefined,
      arrivalAirport:
        arrivalAirports.length == 1 ? arrivalAirports[0] : undefined,
    })
  }

  return flightsWithAirports
}

interface ConvertTripItemsFromOrderItemsParams {
  order: App.Order
  offers: Array<App.AnyOffer>
  currentRegionCode: string
  currentCurrency: string
}

export const convertTripItemsFromOrderItems = async({
  order,
  offers,
  currentRegionCode,
  currentCurrency,
}: ConvertTripItemsFromOrderItemsParams): Promise<
  Array<CreateTripItemRequest>
> => {
  const createTripItemRequests: Array<CreateTripItemRequest> = []

  // Experiences
  if (order.experienceItems.length > 0) {
    // Find all the unique experience ids making sure we don't duplicate.
    const processedExperienceIds: Array<string> = []

    for (const experienceOrderItem of order.experienceItems) {
      if (
        !processedExperienceIds.includes(experienceOrderItem.experienceId) &&
        experienceOrderItem.status !== 'cancelled'
      ) {
        processedExperienceIds.push(experienceOrderItem.experienceId)

        const expItem = await convertOrderExperienceItemToExperienceTripItem({
          orderItem: experienceOrderItem,
          orderId: order.id,
          currentCurrency,
          currentRegionCode,
          orderItemIds: order.experienceItems
            .filter(
              (experienceItem) =>
                experienceItem.experienceId ===
                experienceOrderItem.experienceId,
            )
            .map((experienceItem) => experienceItem.id),
        })

        createTripItemRequests.push(expItem)
      }
    }
  }

  // Flights
  for (const flightOrderItem of order.flightItems) {
    if (flightOrderItem.status !== 'cancelled') {
      const bookedFlights = await getFlightsFromFlightOrder(flightOrderItem)
      for (const flight of bookedFlights) {
        const bookedFlightTripItem = convertFlightItemToFlightTripItem(
          flight,
          flightOrderItem,
        )
        createTripItemRequests.push(bookedFlightTripItem)
      }
    }
  }

  // LE Offers Accommodation and Tours/Cruises
  if (order.items.length > 0) {
    // Find all the unique offer ids making sure we don't duplicate.
    const processedOfferIds: Array<string> = []
    for (const orderItem of order.items) {
      if (
        !processedOfferIds.includes(orderItem.offerId) &&
        orderItem.status !== 'cancelled'
      ) {
        processedOfferIds.push(orderItem.offerId)
        const offer = offers.find(
          (o): o is App.Offer => o?.id === orderItem.offerId,
        )

        if (offer && offer.type === OFFER_TYPE_TOUR) {
          const tourOrCruiseTripItem = convertOrderItemToTourOrCruiseTripItem(
            order,
            orderItem,
            offer,
            currentRegionCode,
            order.items
              .filter((tourItem) => tourItem.offerId === orderItem.offerId)
              .map((tourItem) => tourItem.id),
          )

          createTripItemRequests.push(tourOrCruiseTripItem)
        } else {
          const accommodationTripItem =
            convertStandardOrderItemToAccommodationTripItem(
              order,
              orderItem,
              offer,
              currentRegionCode,
              order.items
                .filter(
                  (accommodationItem) =>
                    accommodationItem.offerId === orderItem.offerId,
                )
                .map((accommodationItem) => accommodationItem.id),
            )

          createTripItemRequests.push(accommodationTripItem)
        }
      }
    }
  }

  // CruisesV2
  if (order.cruiseItems.length > 0) {
    const processedOfferIds: Array<string> = []
    for (const orderItem of order.cruiseItems) {
      if (
        !processedOfferIds.includes(orderItem.cruiseOfferId) &&
        orderItem.status !== 'cancelled'
      ) {
        const offer = offers.find(
          (o): o is App.CruiseOffer => o.id === orderItem.cruiseOfferId,
        )

        if (offer) {
          const cruiseTripItem = convertOrderItemToCruiseV2TripItem(
            order,
            orderItem,
            offer,
            currentRegionCode,
            order.items
              .filter((cruiseItem) => cruiseItem.id === orderItem.cruiseOfferId)
              .map((cruiseItem) => cruiseItem.id),
          )

          createTripItemRequests.push(cruiseTripItem)
        }

        processedOfferIds.push(orderItem.cruiseOfferId)
      }
    }
  }

  // LE TourV2
  if (order.tourItems.length > 0) {
    // Find all the unique tour ids making sure we don't duplicate.
    const processedTourIds: Array<string> = []
    for (const orderItem of order.tourItems) {
      if (
        !processedTourIds.includes(orderItem.tourId) &&
        orderItem.status !== 'cancelled'
      ) {
        // ToDo: Is there a better way of fetching TourV2 Offer?
        const fetchTourV2OfferById = OfferService.getOfferById(
          orderItem.tourId,
          {
            region: currentRegionCode,
          },
        )
        const fetchTourV2SnapshotById = getSnapshotDetailsForTourV2Offer({
          reservationId: orderItem.reservationId,
        })

        await Promise.all([fetchTourV2OfferById, fetchTourV2SnapshotById]).then(
          ([offer, snapshot]) => {
            if (offer && isTourV2Offer(offer)) {
              const tourV2Item = createTourV2Payload(
                order,
                orderItem,
                offer,
                currentRegionCode,
                order.tourItems
                  .filter((tourItem) => tourItem.tourId === orderItem.tourId)
                  .map((tourItem) => tourItem.id),
                snapshot,
              )

              processedTourIds.push(orderItem.tourId)
              createTripItemRequests.push(tourV2Item)
            }
          },
        )
      }
    }
  }

  // Bedbank Accommodation
  for (const bedbankOrderItem of order.bedbankItems) {
    if (bedbankOrderItem.status !== 'cancelled') {
      const convertedBedbankItemToHotelTripItem =
        convertOrderBedbankItemToHotelTripItem(bedbankOrderItem)
      createTripItemRequests.push(convertedBedbankItemToHotelTripItem)
    }
  }

  return createTripItemRequests
}
